import { Box, HStack, Input, Stack, VStack, Text } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { ToggleAtom } from '../../atoms/CustomToggle';
import { CustomText } from '../../atoms';

interface OneTrustGASettingsMoleculeProps {
  configs: {
    enableGoogleAnalytics: boolean;
    gaMeasurementId: string;
    useOneTrust: boolean;
    oneTrustScriptId: string;
    showOneTrustCookieButton: boolean;
    showLogs: boolean;
  };
  onConfigChange: (key: string, value: any) => void;
  setDisableSaveButton: React.Dispatch<React.SetStateAction<boolean>>;
}

export const OneTrustGASettingsMolecule: React.FC<OneTrustGASettingsMoleculeProps> = ({
  configs,
  onConfigChange,
  setDisableSaveButton,
}) => {
  const [enableGoogleAnalytics, setEnableGoogleAnalytics] = React.useState(
    configs.enableGoogleAnalytics,
  );
  const [useOneTrust, setUseOneTrust] = React.useState(configs.useOneTrust);
  const [showOneTrustCookieButton, setShowOneTrustCookieButton] = React.useState(
    configs.showOneTrustCookieButton,
  );
  const [gaMeasurementId, setGaMeasurementId] = React.useState(configs.gaMeasurementId);
  const [oneTrustScriptId, setOneTrustScriptId] = React.useState(configs.oneTrustScriptId);

  const [gaError, setGaError] = React.useState('');
  const [oneTrustError, setOneTrustError] = React.useState('');

  const [showLogs, setshowLogs] = React.useState(configs.showLogs);

  const initialGaMeasurementId = React.useRef(configs.gaMeasurementId);
  const initialOneTrustScriptId = React.useRef(configs.oneTrustScriptId);

  const gaPattern = /^G-[A-Z0-9]{10}$/;

  useEffect(() => {
    let hasError = false;

    // Validate GA
    if (enableGoogleAnalytics) {
      if (!gaMeasurementId) {
        setGaError('GA Measurement ID is required.');
        hasError = true;
      } else if (!gaPattern.test(gaMeasurementId)) {
        setGaError('Invalid format. Example: G-E3TBLJWXF9');
        hasError = true;
      } else {
        setGaError('');
      }
    } else {
      setGaError('');
    }

    // Validate OneTrust
    if (useOneTrust) {
      if (!oneTrustScriptId) {
        setOneTrustError('OneTrust Script ID is required.');
        hasError = true;
      } else {
        setOneTrustError('');
      }
    } else {
      setOneTrustError('');
    }

    setDisableSaveButton(hasError);
  }, [enableGoogleAnalytics, gaMeasurementId, useOneTrust, oneTrustScriptId]);

  const handleGaIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setGaMeasurementId(value);
    onConfigChange('gaMeasurementId', value);

    if (enableGoogleAnalytics) {
      if (!value) {
        setDisableSaveButton(true);
        setGaError('GA Measurement ID is required.');
      } else if (!gaPattern.test(value)) {
        setGaError('Invalid format. Example: G-E3TBLJWXF9');
        setDisableSaveButton(true);
      } else {
        setGaError('');
        setDisableSaveButton(false);
      }
    } else {
      setGaError('');
      setDisableSaveButton(false);
    }
  };

  const handleOneTrustChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setOneTrustScriptId(value);
    onConfigChange('oneTrustScriptId', value);

    if (useOneTrust && !value) {
      setDisableSaveButton(true);
      setOneTrustError('OneTrust Script ID is required.');
    } else {
      setOneTrustError('');
      setDisableSaveButton(false);
    }
  };

  return (
    <Box height="86.5vh" overflowY="auto" border="1px solid #e2e8f0" p={4} borderRadius="md">
      <Stack spacing={6} p={4} mt={4}>
        {/* Enable GA */}
        <HStack p={4} borderWidth="1px" borderRadius="md" mb={4} justifyContent="space-between">
          <ToggleAtom
            justifyContent="space-between"
            label="Enable Google Analytics"
            value={enableGoogleAnalytics}
            onChange={value => {
              setEnableGoogleAnalytics(value);
              onConfigChange('enableGoogleAnalytics', value);
              if (!value) {
                setGaError('');
                setGaMeasurementId(initialGaMeasurementId.current);
                onConfigChange('gaMeasurementId', initialGaMeasurementId.current);
              }
            }}
          />
        </HStack>

        {/* GA Measurement ID */}
        <VStack
          p={4}
          borderWidth="1px"
          borderRadius="md"
          mb={4}
          justifyContent="space-between"
          alignItems="start"
          width="100%"
        >
          <CustomText fontWeight="500">GA Measurement ID</CustomText>
          <Input
            value={gaMeasurementId}
            onChange={handleGaIdChange}
            placeholder="Enter GA Measurement ID"
            isDisabled={!enableGoogleAnalytics}
            isInvalid={!!gaError}
          />
          {gaError && (
            <Text color="red.500" fontSize="sm">
              {gaError}
            </Text>
          )}
        </VStack>

        {/* Use OneTrust */}
        <HStack p={4} borderWidth="1px" borderRadius="md" mb={4} justifyContent="space-between">
          <ToggleAtom
            justifyContent="space-between"
            label="Use OneTrust"
            value={useOneTrust}
            onChange={value => {
              setUseOneTrust(value);
              onConfigChange('useOneTrust', value);
              if (!value) {
                setOneTrustError('');
                setOneTrustScriptId(initialOneTrustScriptId.current);
                onConfigChange('oneTrustScriptId', initialOneTrustScriptId.current);
              }
            }}
          />
        </HStack>

        {/* OneTrust Script ID */}
        <VStack
          p={4}
          borderWidth="1px"
          borderRadius="md"
          mb={4}
          justifyContent="space-between"
          alignItems="start"
          width="100%"
        >
          <CustomText fontWeight="500">OneTrust Script ID</CustomText>
          <Input
            value={oneTrustScriptId}
            onChange={handleOneTrustChange}
            placeholder="Enter OneTrust Script ID"
            isDisabled={!useOneTrust}
            isInvalid={!!oneTrustError}
          />
          {oneTrustError && (
            <Text color="red.500" fontSize="sm">
              {oneTrustError}
            </Text>
          )}
        </VStack>

        {/* Show OneTrust Cookie Button */}
        <HStack p={4} borderWidth="1px" borderRadius="md" mb={4} justifyContent="space-between">
          <ToggleAtom
            justifyContent="space-between"
            label="Show Trust Cookie Button"
            value={showOneTrustCookieButton}
            onChange={value => {
              setShowOneTrustCookieButton(value);
              onConfigChange('showOneTrustCookieButton', value);
            }}
          />
        </HStack>

        {/* Logs Button */}
        <HStack p={4} borderWidth="1px" borderRadius="md" mb={4} justifyContent="space-between">
          <ToggleAtom
            justifyContent="space-between"
            label="Show Logs"
            value={showLogs}
            // eslint-disable-next-line react/jsx-no-bind
            onChange={value => {
              setshowLogs(value);
              onConfigChange('showLogs', value);
            }}
          />
        </HStack>
      </Stack>
    </Box>
  );
};
